// HTTP client for the existing FastAPI API. Mirrors frontend/api_client.py:
// bearer auth, one transparent refresh-and-retry on a 401, and transport
// failures normalised to APIConnectionError so callers have one error type to catch.
import { APIConnectionError, APIError, ModelImportRefused, detailText } from "./errors";

const REFRESH_KEY = "modelbench.refresh";
/** Paths where a 401 is a real answer, not an expired token (frontend/api_client.py _NO_REFRESH_PATHS). */
const NO_REFRESH = new Set([
  "/auth/login", "/auth/signup", "/auth/refresh", "/auth/recover-password",
  "/auth/verify-email", "/auth/resend-verification", "/auth/forgot-password", "/auth/reset-password",
  "/auth/invitations/lookup", "/auth/accept-invite",
]);

export interface RequestOptions {
  query?: Record<string, string | number | boolean | null | undefined>;
  json?: unknown;
  form?: FormData;
  signal?: AbortSignal;
  /** Expected body type. Default: JSON when there is a body, else undefined. */
  as?: "json" | "blob" | "text";
  /** Extra request headers, e.g. an Idempotency-Key. */
  headers?: Record<string, string>;
}

type Fetch = typeof fetch;

export class ApiClient {
  private access: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  /** Called when the refresh token is rejected: the session is over. */
  onSessionExpired: () => void = () => {};

  constructor(
    private baseUrl = import.meta.env?.VITE_API_BASE ?? "/api",
    private doFetch: Fetch = (...a) => fetch(...a),
    private store: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null = safeStorage(),
  ) {}

  get accessToken(): string | null { return this.access; }
  get hasRefreshToken(): boolean { return !!this.refreshToken; }
  private get refreshToken(): string | null {
    try { return this.store?.getItem(REFRESH_KEY) ?? null; } catch { return null; }
  }

  setTokens(access: string, refresh: string): void {
    this.access = access;
    try { this.store?.setItem(REFRESH_KEY, refresh); } catch { /* storage unavailable: session lasts until reload */ }
  }

  clearTokens(): void {
    this.access = null;
    try { this.store?.removeItem(REFRESH_KEY); } catch { /* ignore */ }
  }

  /** Swap the refresh token for a new pair. Concurrent callers share one request. */
  refresh(): Promise<boolean> {
    if (this.refreshing) return this.refreshing;
    const token = this.refreshToken;
    if (!token) return Promise.resolve(false);
    this.refreshing = (async () => {
      try {
        const res = await this.send("POST", "/auth/refresh", { json: { refresh_token: token } }, false);
        if (!res.ok) { this.clearTokens(); this.onSessionExpired(); return false; }
        const data = (await res.json()) as { access_token: string; refresh_token: string };
        this.setTokens(data.access_token, data.refresh_token);
        return true;
      } catch {
        return false; // network trouble is not proof the token is dead
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  private url(path: string, query?: RequestOptions["query"]): string {
    const u = `${this.baseUrl}${path}`;
    if (!query) return u;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null) qs.set(k, String(v));
    const s = qs.toString();
    return s ? `${u}?${s}` : u;
  }

  private async send(method: string, path: string, opts: RequestOptions, auth = true): Promise<Response> {
    const headers: Record<string, string> = { ...opts.headers };
    if (auth && this.access) headers.Authorization = `Bearer ${this.access}`;
    let body: BodyInit | undefined;
    if (opts.form) body = opts.form;
    else if (opts.json !== undefined) { body = JSON.stringify(opts.json); headers["Content-Type"] = "application/json"; }
    try {
      return await this.doFetch(this.url(path, opts.query), { method, headers, body, signal: opts.signal });
    } catch (e) {
      if ((e as Error)?.name === "AbortError") throw e;
      throw new APIConnectionError(`Could not reach the backend (${method} ${path}): ${(e as Error)?.message ?? e}`);
    }
  }

  /** The raw Response, after refresh-and-retry. Throws APIError for status >= 400. */
  async raw(method: string, path: string, opts: RequestOptions = {}): Promise<Response> {
    const used = this.access;
    let res = await this.send(method, path, opts);
    if (res.status === 401 && this.hasRefreshToken && !NO_REFRESH.has(path)) {
      // Another request may already have refreshed while this one was in flight.
      const ok = this.access !== used ? true : await this.refresh();
      if (ok) res = await this.send(method, path, opts);
    }
    if (res.status >= 400) throw await toError(res);
    return res;
  }

  async request<T = unknown>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const res = await this.raw(method, path, opts);
    if (res.status === 204) return undefined as T;
    if (opts.as === "blob") return (await res.blob()) as T;
    if (opts.as === "text") return (await res.text()) as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  get = <T = unknown>(path: string, opts?: RequestOptions) => this.request<T>("GET", path, opts);
  post = <T = unknown>(path: string, opts?: RequestOptions) => this.request<T>("POST", path, opts);
  put = <T = unknown>(path: string, opts?: RequestOptions) => this.request<T>("PUT", path, opts);
  del = <T = unknown>(path: string, opts?: RequestOptions) => this.request<T>("DELETE", path, opts);
}

async function toError(res: Response): Promise<APIError> {
  let detail: unknown = res.statusText;
  try {
    const body = await res.json();
    detail = body?.detail ?? body;
  } catch { /* not JSON */ }
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return new ModelImportRefused(res.status, detail as Record<string, unknown>);
  }
  // ``code`` is the machine-readable reason: X-InSiSo-Auth for a refused account,
  // X-InSiSo-Credits ("insufficient") for work the organization cannot pay for (402).
  return new APIError(res.status, detailText(detail, `Request failed (${res.status})`), undefined,
    res.headers?.get("X-InSiSo-Auth") ?? res.headers?.get("X-InSiSo-Credits") ?? undefined);
}

function safeStorage(): Storage | null {
  try { return typeof localStorage === "undefined" ? null : localStorage; } catch { return null; }
}

/** The one client the app uses. */
export const api = new ApiClient();
